/**
 * Utility functions for places data
 */

/**
 * Sort places alphabetically by place name
 */
export function sortPlaces(
  items: Place[]
): Place[] {
  return [...items].sort((a, b) => (a.placeName || '').localeCompare(b.placeName || ''));
}

/**
 * Get place by ID
 */
export function getPlaceById(
  items: Place[],
  id: string
): Place | undefined {
  return items.find(item => item.id === id);
}

/**
 * Normalize a place name for fuzzy matching
 * Handles variations like:
 * - "Antioch (on the Orontes)" vs "Antioch on the Orontes"
 * - "Antioch of Pisidia" vs "Antioch in Pisidia"
 * - "Sea of Galilee" vs "Lake of Gennesaret"
 */
function normalizePlaceName(name: string): string {
  return name
    .toLowerCase()
    .replace(/\s*\([^)]*\)\s*/g, ' ') // Remove parentheses and content: "(on the Orontes)" -> " "
    .replace(/\s+(of|in|on|at|near)\s+/g, ' ') // Normalize prepositions: "of Pisidia" -> " Pisidia"
    .replace(/\s+/g, ' ') // Normalize whitespace
    .trim();
}

/**
 * Remove parentheses from a place name for matching
 * "Antioch (on the Orontes)" -> "Antioch on the Orontes"
 */
function removeParentheses(name: string): string {
  return name.replace(/\s*\(([^)]+)\)\s*/g, ' $1').replace(/\s+/g, ' ').trim();
}

/**
 * Generate alternate name variations for fuzzy matching
 * Creates variations by:
 * - Removing parentheses: "Antioch (on the Orontes)" -> "Antioch on the Orontes"
 * - Swapping prepositions: "Antioch of Pisidia" -> "Antioch in Pisidia", "Antioch at Pisidia"
 */
function generateNameVariations(name: string): string[] {
  const variations = [name];

  // If name has parentheses, add version without them
  if (name.includes('(') && name.includes(')')) {
    const withoutParens = removeParentheses(name);
    variations.push(withoutParens);
  }

  // If name has "of", add versions with "in", "at", "on"
  if (name.includes(' of ')) {
    variations.push(name.replace(' of ', ' in '));
    variations.push(name.replace(' of ', ' at '));
    variations.push(name.replace(' of ', ' on '));
  }

  // If name has "in", add versions with "of", "at"
  if (name.includes(' in ')) {
    variations.push(name.replace(' in ', ' of '));
    variations.push(name.replace(' in ', ' at '));
  }

  // If name has "on", add versions with "of", "at"
  if (name.includes(' on ')) {
    variations.push(name.replace(' on ', ' of '));
    variations.push(name.replace(' on ', ' at '));
  }

  return variations;
}

/**
 * Get place by name with fuzzy matching
 * Tries multiple strategies:
 * 1. Exact match on placeName
 * 2. Case-insensitive match on placeName
 * 3. Match against alternateNames array
 * 4. Match with parentheses removed on both sides
 * 5. Try name variations (parentheses, prepositions)
 * 6. Fuzzy match (normalized) on placeName and alternateNames
 */
export function getPlaceByName(
  items: Place[],
  placeName: string
): Place | undefined {
  if (!placeName) return undefined;

  // 1. Try exact match (case-sensitive)
  let match = items.find(item => item.placeName === placeName);
  if (match) return match;

  // 2. Try case-insensitive match
  const lowerName = placeName.toLowerCase();
  match = items.find(item => item.placeName?.toLowerCase() === lowerName);
  if (match) return match;

  // 3. Try alternateNames (exact and case-insensitive)
  match = items.find(item =>
    item.alternateNames?.some(alt =>
      alt && (alt === placeName || alt.toLowerCase() === lowerName)
    )
  );
  if (match) return match;

  // 4. Try matching with parentheses removed on both sides
  // This handles "Antioch on the Orontes" vs "Antioch (on the Orontes)" in either direction
  const searchWithoutParens = removeParentheses(placeName).toLowerCase();
  match = items.find(item => {
    // Check against placeName
    if (item.placeName && removeParentheses(item.placeName).toLowerCase() === searchWithoutParens) {
      return true;
    }
    // Check against alternateNames
    return item.alternateNames?.some(alt =>
      alt && removeParentheses(alt).toLowerCase() === searchWithoutParens
    );
  });
  if (match) return match;

  // 5. Try name variations (handles "Antioch on the Orontes" vs "Antioch (on the Orontes)")
  const searchVariations = generateNameVariations(placeName);
  for (const variation of searchVariations) {
    const variationLower = variation.toLowerCase();
    match = items.find(item => {
      // Check against placeName
      if (item.placeName?.toLowerCase() === variationLower) {
        return true;
      }
      // Check against alternateNames
      return item.alternateNames?.some(alt =>
        alt && alt.toLowerCase() === variationLower
      );
    });
    if (match) return match;
  }

  // 6. Try fuzzy matching (normalized - removes all prepositions and parentheses)
  const normalizedSearch = normalizePlaceName(placeName);
  match = items.find(item => {
    // Check normalized placeName
    if (item.placeName && normalizePlaceName(item.placeName) === normalizedSearch) {
      return true;
    }
    // Check normalized alternateNames
    return item.alternateNames?.some(alt =>
      alt && normalizePlaceName(alt) === normalizedSearch
    );
  });

  return match;
}

/**
 * Filter places by importance level
 */
export function filterPlacesByImportance(
  places: Place[],
  importance: Importance
): Place[] {
  return places.filter(place => place.importance === importance);
}

/**
 * Get high importance places
 */
export function getHighImportancePlaces(
  places: Place[]
): Place[] {
  return filterPlacesByImportance(places, 'high');
}

/**
 * Filter places by region
 */
export function filterPlacesByRegion(
  places: Place[],
  region: string
): Place[] {
  return places.filter(place => place.region === region);
}

/**
 * Filter places by type
 */
export function filterPlacesByType(
  places: Place[],
  type: Place['type']
): Place[] {
  return places.filter(place => place.type === type);
}

